import type { fabric } from 'fabric';
import type { FabricCanvasData, ProjectPage } from '@canvas/shared-types';
import {
  type ExportFormat,
  withTransparentArtboard,
} from '@/lib/exportCanvas';
import { withOffscreenCanvas } from '@/lib/offscreenCanvas';
import { pdfFromJpegs } from '@/lib/pdfStore';
import { zipStore } from '@/lib/zipStore';

function sanitizeFilename(name: string): string {
  const cleaned = name
    .trim()
    .replace(/[<>:"/\\|?*\u0000-\u001f]/g, '-')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');
  return cleaned || 'page';
}

function dataUrlToUint8(dataUrl: string): Uint8Array {
  const comma = dataUrl.indexOf(',');
  const b64 = comma >= 0 ? dataUrl.slice(comma + 1) : dataUrl;
  const bin = atob(b64);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i += 1) out[i] = bin.charCodeAt(i);
  return out;
}

function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.rel = 'noopener';
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

function canvasToSvgString(canvas: fabric.Canvas): string {
  return canvas.toSVG({
    width: String(canvas.getWidth()),
    height: String(canvas.getHeight()),
    viewBox: {
      x: 0,
      y: 0,
      width: canvas.getWidth(),
      height: canvas.getHeight(),
    },
  });
}

async function renderPageBytes(
  page: FabricCanvasData,
  options: {
    format: ExportFormat;
    scale: number;
    quality: number;
    liveCanvas?: fabric.Canvas | null;
    useLive?: boolean;
    transparentBackground?: boolean;
  }
): Promise<Uint8Array> {
  const { format, scale, quality, liveCanvas, useLive } = options;
  const transparent =
    Boolean(options.transparentBackground) &&
    (format === 'png' || format === 'svg');

  if (useLive && liveCanvas) {
    return withTransparentArtboard(liveCanvas, transparent, async () => {
      if (format === 'svg') {
        return new TextEncoder().encode(canvasToSvgString(liveCanvas));
      }

      const mime = format === 'jpg' ? 'jpeg' : 'png';
      const prevBg = liveCanvas.backgroundColor;
      if (mime === 'jpeg' && (!prevBg || prevBg === 'transparent' || prevBg === '')) {
        await new Promise<void>((resolve) => {
          liveCanvas.setBackgroundColor('#ffffff', () => resolve());
        });
      }
      const dataUrl = liveCanvas.toDataURL({
        format: mime,
        quality: mime === 'jpeg' ? quality : 1,
        multiplier: scale,
        enableRetinaScaling: true,
      });
      if (mime === 'jpeg' && prevBg !== liveCanvas.backgroundColor) {
        await new Promise<void>((resolve) => {
          liveCanvas.setBackgroundColor(prevBg as string, () => {
            liveCanvas.requestRenderAll();
            resolve();
          });
        });
      }
      return dataUrlToUint8(dataUrl);
    });
  }

  return withOffscreenCanvas(page, transparent, async (canvas) => {
    if (format === 'svg') {
      return new TextEncoder().encode(canvasToSvgString(canvas));
    }

    const mime = format === 'jpg' ? 'jpeg' : 'png';
    if (mime === 'jpeg') {
      canvas.setBackgroundColor('#ffffff', () => undefined);
      canvas.renderAll();
    }
    const dataUrl = canvas.toDataURL({
      format: mime,
      quality: mime === 'jpeg' ? quality : 1,
      multiplier: scale,
      enableRetinaScaling: false,
    });
    return dataUrlToUint8(dataUrl);
  });
}

export type ExportScope = 'active' | 'all';

function pagePixelSize(
  page: FabricCanvasData,
  scale: number,
  liveCanvas: fabric.Canvas | null,
  useLive: boolean
): { width: number; height: number } {
  if (useLive && liveCanvas) {
    return {
      width: Math.round(liveCanvas.getWidth() * scale),
      height: Math.round(liveCanvas.getHeight() * scale),
    };
  }
  return {
    width: Math.round((page.width || 1200) * scale),
    height: Math.round((page.height || 800) * scale),
  };
}

/**
 * Export one page or all pages.
 * - PNG/JPG/SVG: multi-page downloads as a ZIP
 * - PDF: always a single multi-page PDF (JPEG-embedded pages)
 * Counts as a single export action for freemium.
 */
export async function exportProjectPages(options: {
  pages: ProjectPage[];
  activePageId: string;
  liveCanvas: fabric.Canvas | null;
  format: ExportFormat;
  scale?: number;
  quality?: number;
  projectName?: string;
  scope: ExportScope;
  transparentBackground?: boolean;
}): Promise<{ fileCount: number; zipped: boolean }> {
  const {
    pages,
    activePageId,
    liveCanvas,
    format,
    scale = 1,
    quality = 0.92,
    projectName = 'craftara-export',
    scope,
    transparentBackground = false,
  } = options;

  const selected =
    scope === 'all'
      ? pages
      : pages.filter((p) => p.pageId === activePageId).length
        ? pages.filter((p) => p.pageId === activePageId)
        : pages.slice(0, 1);

  if (!selected.length) {
    throw new Error('No pages to export');
  }

  const base = sanitizeFilename(projectName);

  // PDF: rasterize pages as JPEG and pack into one document
  if (format === 'pdf') {
    const pdfPages = [];
    for (let i = 0; i < selected.length; i += 1) {
      const page = selected[i];
      const useLive = page.pageId === activePageId;
      const jpeg = await renderPageBytes(page.canvasData, {
        format: 'jpg',
        scale,
        quality,
        liveCanvas,
        useLive,
        transparentBackground: false,
      });
      const size = pagePixelSize(page.canvasData, scale, liveCanvas, useLive);
      pdfPages.push({ jpeg, ...size });
    }
    downloadBlob(pdfFromJpegs(pdfPages), `${base}.pdf`);
    return { fileCount: selected.length, zipped: false };
  }

  const ext = format === 'jpg' ? 'jpg' : format;
  const entries: Array<{ name: string; data: Uint8Array }> = [];

  for (let i = 0; i < selected.length; i += 1) {
    const page = selected[i];
    const useLive = page.pageId === activePageId;
    const bytes = await renderPageBytes(page.canvasData, {
      format,
      scale,
      quality,
      liveCanvas,
      useLive,
      transparentBackground,
    });
    const pageName = sanitizeFilename(page.name || `page-${i + 1}`);
    const fileName =
      selected.length === 1
        ? `${base}.${ext}`
        : `${base}/${String(i + 1).padStart(2, '0')}-${pageName}.${ext}`;
    entries.push({ name: fileName, data: bytes });
  }

  if (entries.length === 1) {
    const entry = entries[0];
    const type =
      format === 'svg'
        ? 'image/svg+xml'
        : format === 'jpg'
          ? 'image/jpeg'
          : 'image/png';
    downloadBlob(new Blob([Uint8Array.from(entry.data)], { type }), entry.name);
    return { fileCount: 1, zipped: false };
  }

  downloadBlob(zipStore(entries), `${base}-pages.zip`);
  return { fileCount: entries.length, zipped: true };
}
